/**
 * Paste cleanup utilities.
 *
 * Preserves semantic formatting (bold, italic, underline, strikethrough, links)
 * and structural blocks (headings, paragraphs, lists) while stripping
 * template-breaking inline styles, junk attributes, and external markup.
 *
 * The motivating bug: pasted inline `style` attributes (font-size, color,
 * font-family, etc.) override the destination template's CSS and "stick" to
 * the field. After cleanup the only inline style that survives is
 * `text-align: left` on block elements — applied deliberately because the
 * editor's default block alignment is center, which is wrong for body content.
 */

import { VARIABLE_PILL_ATTR } from "@/shared/utils/variable-pill";

// ─── Internal-Copy Detection & Wrapper Stripping ─────────────────────

/**
 * CSS selectors that uniquely identify a clipboard copy from inside Storydoc:
 * a wrapper element of a text field, or a Storydoc-internal feature
 * (icon, emoji, RKPI animation, highlight, text-animation).
 *
 * Detection uses real DOM queries instead of substring matching so that an
 * external paste containing the literal text "data-element-name" (e.g. a code
 * snippet about Storydoc) doesn't get misclassified as internal.
 */
const STORYDOC_CLIPBOARD_SELECTOR = [
  "[data-element-name]",
  "[data-layer-id]",
  "[data-layer-type]",
  ".rich-text-wrapper",
  ".element-wrapper",
  ".elementContainer",
  "[data-rkpi-active]",
  "[data-highlight-active]",
  "[data-is-remix-icon]",
  "[data-is-font-awesome-icon]",
  "em-emoji",
  "[data-text-animation-active]",
  // Deliberately NOT a pill selector: a pill-only clipboard (copy a variable,
  // paste it elsewhere) goes through the external cleaner, which keeps the
  // pill's class and data-variable-name (see stripExternalAttributes) and the
  // paste handler re-hydrates it. Classifying on a pill would let any outside
  // HTML that happens to carry the attribute skip the cleaner entirely.
].join(", ");

/** Selectors for Storydoc layout wrappers — unwrapped before re-insert so
 *  copying between text fields doesn't nest one element container inside
 *  another. Inner formatting (icons, highlights, fonts) is preserved. */
const STORYDOC_WRAPPER_SELECTOR = [
  "[data-element-name]",
  "[data-layer-id]",
  "[data-layer-type]",
  ".rich-text-wrapper",
  ".element-wrapper",
  ".elementContainer",
].join(", ");

export const isInternalStorydocPaste = (html: string): boolean => {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelector(STORYDOC_CLIPBOARD_SELECTOR) !== null;
};

/**
 * Strip Storydoc layout wrappers (element containers, rich-text-wrappers)
 * from internal-copy HTML, leaving the actual content (paragraphs, headings,
 * icons, highlights) intact and ready to insert into the destination editor.
 */
export const extractInternalContent = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  // Iterate until no wrappers remain — unwrapping a parent re-parents
  // children that may themselves be wrappers (rare but possible when the
  // clipboard contains nested element containers).
  let safety = 5;
  while (safety-- > 0) {
    const wrappers = doc.querySelectorAll(STORYDOC_WRAPPER_SELECTOR);
    if (wrappers.length === 0) break;
    for (const el of Array.from(wrappers)) {
      el.replaceWith(...Array.from(el.childNodes));
    }
  }
  return doc.body.innerHTML.trim();
};

// ─── Source Detection ────────────────────────────────────────────────

type PasteSource = "google-docs" | "ms-word" | "web";

const detectPasteSource = (html: string): PasteSource => {
  if (html.includes("docs-internal-guid")) return "google-docs";
  if (
    html.includes("mso-") ||
    html.includes("urn:schemas-microsoft-com:office") ||
    html.includes('class="Mso')
  )
    return "ms-word";
  return "web";
};

// ─── Source-Specific Cleaners ────────────────────────────────────────

const cleanGoogleDocsHtml = (doc: Document) => {
  // Google wraps everything in <b style="font-weight:normal"> — unwrap those
  for (const b of Array.from(doc.querySelectorAll("b"))) {
    const style = b.getAttribute("style") || "";
    const match = style.match(/font-weight:\s*([^;]+)/i);
    if (match) {
      const val = match[1].trim().toLowerCase();
      const num = Number.parseInt(val);
      const isActuallyNormal =
        val === "normal" || (!Number.isNaN(num) && num < 500);
      if (isActuallyNormal) {
        b.replaceWith(...Array.from(b.childNodes));
      }
    }
  }

  // Strip Google-specific attributes
  for (const el of Array.from(doc.querySelectorAll("[id]"))) {
    if (el.id.startsWith("docs-internal-guid")) el.removeAttribute("id");
  }
  for (const el of Array.from(doc.querySelectorAll("[data-docs-delta]"))) {
    el.removeAttribute("data-docs-delta");
  }
  for (const el of Array.from(doc.querySelectorAll('[dir="ltr"]'))) {
    el.removeAttribute("dir");
  }

  // Remove <meta> and <style> tags
  for (const tag of Array.from(doc.querySelectorAll("meta, style"))) {
    tag.remove();
  }
};

const cleanMsWordHtml = (doc: Document) => {
  // Unwrap <o:p> elements
  for (const op of Array.from(doc.querySelectorAll("o\\:p"))) {
    op.replaceWith(...Array.from(op.childNodes));
  }

  // Strip Word-specific class and lang attrs
  for (const el of Array.from(doc.querySelectorAll('[class^="Mso"]'))) {
    el.removeAttribute("class");
  }
  for (const el of Array.from(doc.querySelectorAll("[lang]"))) {
    el.removeAttribute("lang");
  }
};

// ─── Semantic Style Preservation ─────────────────────────────────────

/**
 * Convert inline CSS styles to semantic HTML tags before stripping styles.
 * Preserves bold, italic, underline, and strikethrough from any paste source.
 */
const preserveSemanticStyles = (doc: Document) => {
  for (const el of Array.from(doc.querySelectorAll("[style]"))) {
    if (!(el instanceof HTMLElement)) continue;

    const styleAttr = el.getAttribute("style") || "";

    const fontWeightMatch = styleAttr.match(/font-weight:\s*([^;]+)/i);
    if (fontWeightMatch) {
      const val = fontWeightMatch[1].trim();
      const num = Number.parseInt(val);
      const isBold = !Number.isNaN(num)
        ? num >= 600
        : ["bold", "bolder"].includes(val.toLowerCase());

      if (isBold) {
        const strong = doc.createElement("strong");
        while (el.firstChild) strong.appendChild(el.firstChild);
        el.appendChild(strong);
      }
    }

    const fontStyleMatch = styleAttr.match(/font-style:\s*([^;]+)/i);
    if (fontStyleMatch && fontStyleMatch[1].trim().toLowerCase() === "italic") {
      const em = doc.createElement("em");
      while (el.firstChild) em.appendChild(el.firstChild);
      el.appendChild(em);
    }

    const textDecMatch = styleAttr.match(/text-decoration:\s*([^;]+)/i);
    if (textDecMatch) {
      const decValue = textDecMatch[1].trim().toLowerCase();
      const hasUnderline = decValue.includes("underline");
      const hasLineThrough = decValue.includes("line-through");

      if (hasUnderline || hasLineThrough) {
        // Build wrappers inside-out: underline first, then line-through around it
        // to avoid double-wrapping when both are present.
        const children = Array.from(el.childNodes);

        if (hasUnderline) {
          const u = doc.createElement("u");
          for (const child of children) u.appendChild(child);
          if (hasLineThrough) {
            const s = doc.createElement("s");
            s.appendChild(u);
            el.appendChild(s);
          } else {
            el.appendChild(u);
          }
        } else if (hasLineThrough) {
          const s = doc.createElement("s");
          for (const child of children) s.appendChild(child);
          el.appendChild(s);
        }
      }
    }
  }
};

// ─── Universal Cleanup ──────────────────────────────────────────────

/** Strip every inline `style` attribute from the document. */
const stripInlineStyles = (doc: Document) => {
  for (const el of Array.from(doc.querySelectorAll("[style]"))) {
    el.removeAttribute("style");
  }
};

const stripExternalAttributes = (doc: Document) => {
  for (const el of Array.from(doc.querySelectorAll("*"))) {
    if (!(el instanceof HTMLElement)) continue;
    const toRemove: string[] = [];

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === "class") {
        // A pill keeps its class — it is what makes the span an atom.
        if (!el.hasAttribute(VARIABLE_PILL_ATTR)) toRemove.push(attr.name);
      } else if (name === "id") {
        toRemove.push(attr.name);
      } else if (name === "dir" && attr.value !== "rtl") {
        toRemove.push(attr.name);
      } else if (name.startsWith("data-") && !isStorydocAttr(name)) {
        toRemove.push(attr.name);
      }
    }

    for (const name of toRemove) {
      el.removeAttribute(name);
    }
  }
};

/** Storydoc-internal data-* prefixes — features, not styling. Preserved. */
const STORYDOC_DATA_PREFIXES = [
  "data-rkpi-",
  "data-highlight-",
  "data-is-remix-icon",
  "data-is-font-awesome-icon",
  "data-text-animation-",
  VARIABLE_PILL_ATTR,
];

const isStorydocAttr = (name: string): boolean =>
  STORYDOC_DATA_PREFIXES.some((prefix) => name.startsWith(prefix));

/**
 * Flatten block-structured HTML into inline content for insertion inside a list
 * item. List containers are unwrapped and every top-level block (`<p>`, heading,
 * `<li>`, …) is replaced by its children, with a `<br>` between consecutive
 * blocks so multi-paragraph pastes keep their line breaks.
 *
 * Froala v5's `html.insert` throws (and silently drops the paste) when block
 * content lands in a list item whose caret is nested in a child element — it
 * unwraps the block to a leading text node, then passes that string to
 * `querySelectorAll` as a selector. Inserting inline content avoids that path.
 * See Monday #12279122681.
 */
export const blockHtmlToInline = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  for (const list of Array.from(doc.querySelectorAll("ul, ol"))) {
    list.replaceWith(...Array.from(list.childNodes));
  }

  const blocks = Array.from(doc.body.children).filter((el) =>
    BLOCK_LEVEL_TAGS.has(el.tagName.toLowerCase()),
  );
  blocks.forEach((el, index) => {
    if (index > 0 && el.parentNode) {
      el.parentNode.insertBefore(doc.createElement("br"), el);
    }
    el.replaceWith(...Array.from(el.childNodes));
  });

  return doc.body.innerHTML.trim();
};

const BLOCK_SELECTORS = "h1, h2, h3, h4, h5, h6, p, li, div, blockquote";

/** Block-level tags that cannot be validly nested inside <p>. */
const BLOCK_LEVEL_TAGS = new Set([
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "table",
  "blockquote",
  "pre",
  "hr",
]);

/**
 * Normalize block elements to Storydoc heading hierarchy:
 * h1–h4 are preserved (each maps to a Storydoc text style); h5/h6/div/blockquote
 * are converted to <p>, or unwrapped if they contain block-level children
 * (avoids invalid nesting like <p><p>...</p></p>).
 *
 * Forces `text-align: left` on every preserved/created block — the editor's
 * default block alignment is center, which looks wrong for pasted body content.
 * Runs after `stripInlineStyles` so this is the only inline style that survives.
 */
const normalizeBlocks = (doc: Document) => {
  for (const el of Array.from(doc.querySelectorAll(BLOCK_SELECTORS))) {
    if (!(el instanceof HTMLElement)) continue;
    if (!doc.body.contains(el)) continue;

    const tag = el.tagName.toLowerCase();
    if (
      tag === "h1" ||
      tag === "h2" ||
      tag === "h3" ||
      tag === "h4" ||
      tag === "p" ||
      tag === "li"
    ) {
      el.style.textAlign = "left";
      continue;
    }

    // h5/h6/div/blockquote
    const hasBlockChildren = Array.from(el.children).some((child) =>
      BLOCK_LEVEL_TAGS.has(child.tagName.toLowerCase()),
    );
    if (hasBlockChildren) {
      el.replaceWith(...Array.from(el.childNodes));
    } else {
      const p = doc.createElement("p");
      while (el.firstChild) p.appendChild(el.firstChild);
      p.style.textAlign = "left";
      el.replaceWith(p);
    }
  }
};

/** Remove <p> elements that contain only whitespace and/or <br> tags. */
const removeEmptyParagraphs = (doc: Document) => {
  for (const p of Array.from(doc.querySelectorAll("p"))) {
    if (!(p instanceof HTMLElement)) continue;
    const content = p.innerHTML.replace(/<br\s*\/?>/gi, "").trim();
    if (!content) p.remove();
  }
};

const flattenEmptySpans = (doc: Document) => {
  // Bottom-up: process deepest first
  const elements = Array.from(doc.querySelectorAll("span"));
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i];
    if (el instanceof HTMLElement && el.attributes.length === 0) {
      el.replaceWith(...Array.from(el.childNodes));
    }
  }
};

/** Legacy <font> tags carry visual properties (color, size, face) as
 *  attributes that bypass `stripInlineStyles`. Unwrap unconditionally. */
const unwrapFontTags = (doc: Document) => {
  for (const el of Array.from(doc.querySelectorAll("font"))) {
    el.replaceWith(...Array.from(el.childNodes));
  }
};

/** Wrap stray text/inline nodes at body level in <p> — happens when a <div>
 *  with mixed inline+block children gets unwrapped by `normalizeBlocks`. */
const wrapOrphanInlineNodes = (doc: Document) => {
  const orphans = Array.from(doc.body.childNodes).filter((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? "").trim().length > 0;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const tag = (node as Element).tagName.toLowerCase();
    return !BLOCK_LEVEL_TAGS.has(tag);
  });

  for (const node of orphans) {
    const p = doc.createElement("p");
    p.style.textAlign = "left";
    node.replaceWith(p);
    p.appendChild(node);
  }
};

const MEDIA_TAGS = "img, svg, script, picture, video, canvas, iframe";

const removeMediaElements = (doc: Document) => {
  for (const el of Array.from(doc.querySelectorAll(MEDIA_TAGS))) {
    el.remove();
  }
};

// ─── Link Cleanup ────────────────────────────────────────────────────

/** Decode and normalize an href to detect obfuscated unsafe protocols. */
const isUnsafeProtocol = (href: string): boolean => {
  try {
    const decoded = decodeURIComponent(href);
    const normalized = decoded.replace(/[\s\0]+/g, "").toLowerCase();
    return (
      normalized.startsWith("javascript:") || normalized.startsWith("vbscript:")
    );
  } catch {
    return href
      .replace(/[\s\0]+/g, "")
      .toLowerCase()
      .startsWith("javascript:");
  }
};

const cleanLinks = (doc: Document) => {
  for (const link of Array.from(doc.querySelectorAll("a"))) {
    const href = link.getAttribute("href") || "";

    if (isUnsafeProtocol(href)) {
      link.replaceWith(...Array.from(link.childNodes));
      continue;
    }

    const allowedAttrs = new Set(["href", "target"]);
    for (const attr of Array.from(link.attributes)) {
      if (!allowedAttrs.has(attr.name.toLowerCase())) {
        link.removeAttribute(attr.name);
      }
    }

    if (href && !href.startsWith("#")) {
      link.setAttribute("target", "_blank");
    }
  }
};

// ─── Pre-DOM Regex Cleaners ──────────────────────────────────────────

/** Strip Word conditional comments: <!--[if ...]>...<![endif]--> */
const stripWordConditionalComments = (html: string): string =>
  html.replace(/<!--\[if\s[\s\S]*?<!\[endif\]-->/gi, "");

// ─── Orchestrator ────────────────────────────────────────────────────

/**
 * Clipboard HTML as the content a paste inserts: a copy from another
 * Storydoc text keeps everything, anything else is cleaned. One routing for
 * the cell editor's paste and a paste onto a selected table cell.
 */
export const pastedHtmlToContent = (html: string): string =>
  isInternalStorydocPaste(html)
    ? extractInternalContent(html)
    : cleanPastedHtml(html);

export const cleanPastedHtml = (html: string): string => {
  const processed = stripWordConditionalComments(html);
  const doc = new DOMParser().parseFromString(processed, "text/html");

  const source = detectPasteSource(processed);
  if (source === "google-docs") cleanGoogleDocsHtml(doc);
  else if (source === "ms-word") cleanMsWordHtml(doc);

  removeMediaElements(doc);
  unwrapFontTags(doc);
  preserveSemanticStyles(doc);
  stripInlineStyles(doc);
  stripExternalAttributes(doc);
  cleanLinks(doc);
  normalizeBlocks(doc);
  wrapOrphanInlineNodes(doc);
  flattenEmptySpans(doc);
  removeEmptyParagraphs(doc);

  // Strip clipboard fragment comments — these crash Froala's querySelectorAll wrapper
  return doc.body.innerHTML
    .replace(/<!--StartFragment-->/gi, "")
    .replace(/<!--EndFragment-->/gi, "")
    .trim();
};
